import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BACKEND_URL as API } from '../../apiConfig';
import { getInternalFleetCopy } from '../../pages/internal/internalFleetI18n';
import DispatchDocumentSettings from './DispatchDocumentSettings';

// 权限列表定义
const PERMISSION_OPTIONS = [
  { id: 'vehicles_view', label: '查看车辆', group: '车辆管理' },
  { id: 'vehicles_edit', label: '编辑车辆', group: '车辆管理' },
  { id: 'drivers_view', label: '查看司机', group: '司机管理' },
  { id: 'drivers_edit', label: '编辑司机', group: '司机管理' },
  { id: 'finances_view', label: '查看财务', group: '财务管理' },
  { id: 'finances_edit', label: '编辑财务', group: '财务管理' },
];

const FleetSettings = ({ user, onUserUpdate, language = 'zh' }) => {
  const copy = getInternalFleetCopy(language);
  const t = copy.settings;
  const common = copy.common;
  const [activeSection, setActiveSection] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    notification_emails: [],
    license_expiry_notify: true,
    insurance_expiry_notify: true,
    inspection_expiry_notify: true,
    health_check_expiry_notify: true,
  });
  const [newEmail, setNewEmail] = useState('');
  const [sendingAlert, setSendingAlert] = useState(false);

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [userForm, setUserForm] = useState({
    username: '',
    password: '',
    name: '',
    role: 'user',
    permissions: [],
  });

  const sections = user?.role === 'admin' 
    ? [
        { id: 'users', label: t.users, icon: '👥' },
        { id: 'email', label: t.email, icon: '📧' },
        { id: 'password', label: t.password, icon: '🔑' },
        { id: 'company', label: t.company, icon: '🏢' },
        { id: 'dispatch-documents', label: '配車指示書・車庫', icon: '📄' },
        { id: 'system', label: t.system, icon: '⚙️' },
      ]
    : [
        { id: 'password', label: t.password, icon: '🔑' },
        { id: 'company', label: t.company, icon: '🏢' },
      ];

  const [settings, setSettings] = useState({
    companyName: '湘南国際株式会社',
    companyNameKana: 'ショウナンコクサイ',
    phone: '03-1234-5678',
    email: 'info@shonan-kokusai.com',
    address: '東京都渋谷区...',
  });

  const fetchData = useCallback(async () => {
    try {
      // 获取公司信息（所有用户都可以查看）
      const companyRes = await axios.get(`${API}/api/internal/settings/company`);
      setSettings(companyRes.data || {
        companyName: '湘南国際株式会社',
        companyNameKana: 'ショウナンコクサイ',
        phone: '03-1234-5678',
        email: 'info@shonan-kokusai.com',
        address: '東京都渋谷区...',
      });
      
      if (user?.role === 'admin') {
        const [usersRes, emailRes] = await Promise.all([
          axios.get(`${API}/api/internal/users`),
          axios.get(`${API}/api/internal/settings/email`),
        ]);
        setUsers(usersRes.data || []);
        setEmailSettings(emailRes.data || {
          notification_emails: [],
          license_expiry_notify: true,
          insurance_expiry_notify: true,
          inspection_expiry_notify: true,
          health_check_expiry_notify: true,
        });
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // User management functions
  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.put(`${API}/api/internal/users/${editingUser.id}`, {
          name: userForm.name,
          role: userForm.role,
          permissions: userForm.permissions,
        });
      } else {
        await axios.post(`${API}/api/internal/users`, userForm);
      }
      fetchData();
      setShowUserModal(false);
      resetUserForm();
    } catch (error) {
      console.error('Failed to save user:', error);
      alert('保存失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUserStatusChange = async (userId, newStatus) => {
    try {
      await axios.put(`${API}/api/internal/users/${userId}`, { status: newStatus });
      fetchData();
    } catch (error) {
      console.error('Failed to update user status:', error);
      alert('操作失败');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('确定要删除这个用户吗？')) {
      try {
        await axios.delete(`${API}/api/internal/users/${userId}`);
        fetchData();
      } catch (error) {
        console.error('Failed to delete user:', error);
        alert('删除失败');
      }
    }
  };

  const resetUserForm = () => {
    setUserForm({ username: '', password: '', name: '', role: 'user', permissions: [] });
    setEditingUser(null);
  };

  const openEditUserModal = (u) => {
    setEditingUser(u);
    setUserForm({
      username: u.username,
      password: '',
      name: u.name,
      role: u.role,
      permissions: u.permissions || [],
    });
    setShowUserModal(true);
  };

  const togglePermission = (permId) => {
    const newPerms = userForm.permissions.includes(permId)
      ? userForm.permissions.filter(p => p !== permId)
      : [...userForm.permissions, permId];
    setUserForm({ ...userForm, permissions: newPerms });
  };

  // Password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      alert('两次输入的新密码不一致');
      return;
    }
    try {
      await axios.put(`${API}/api/internal/auth/change-password`, {
        old_password: passwordForm.old_password,
        new_password: passwordForm.new_password
      });
      alert('密码修改成功');
      setPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      alert('修改失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Email settings functions
  const addEmail = () => {
    if (newEmail && !emailSettings.notification_emails.includes(newEmail)) {
      setEmailSettings({
        ...emailSettings,
        notification_emails: [...emailSettings.notification_emails, newEmail]
      });
      setNewEmail('');
    }
  };

  const removeEmail = (email) => {
    setEmailSettings({
      ...emailSettings,
      notification_emails: emailSettings.notification_emails.filter(e => e !== email)
    });
  };

  const saveEmailSettings = async () => {
    try {
      await axios.put(`${API}/api/internal/settings/email`, emailSettings);
      alert('邮件设置已保存');
    } catch (error) {
      console.error('Failed to save email settings:', error);
      alert('保存失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  const sendExpiryAlerts = async () => {
    if (emailSettings.notification_emails.length === 0) {
      alert('请先添加接收邮箱');
      return;
    }
    
    setSendingAlert(true);
    try {
      const res = await axios.post(`${API}/api/internal/notifications/send-expiry-alerts`);
      alert(`${res.data.message}\n\n发送到: ${res.data.recipients.join(', ')}`);
    } catch (error) {
      console.error('Failed to send alerts:', error);
      alert('发送失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingAlert(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/api/internal/settings/company`, settings);
      alert('公司信息已保存');
    } catch (error) {
      console.error('Failed to save company settings:', error);
      alert('保存失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  // 权限分组显示
  const groupedPermissions = PERMISSION_OPTIONS.reduce((acc, perm) => {
    if (!acc[perm.group]) acc[perm.group] = [];
    acc[perm.group].push(perm);
    return acc;
  }, {});

  return (
    <div className="internal-page-skin flex flex-col lg:flex-row gap-4 lg:gap-6 min-w-0">
      {/* Settings Sidebar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:block w-full lg:w-64 lg:shrink-0 gap-2 lg:space-y-2">
        {sections.map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
              activeSection === section.id
                ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg shadow-blue-500/30'
                : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 border border-slate-700'
            }`}
          >
            <span className="text-xl">{section.icon}</span>
            <span className="font-medium">{section.label}</span>
          </button>
        ))}
      </div>

      {/* Settings Content */}
      <div className="flex-1 min-w-0 bg-slate-800 rounded-xl border border-slate-700 p-3 sm:p-6">
        {/* User Management */}
        {activeSection === 'users' && user?.role === 'admin' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-white text-xl font-bold">{t.users}</h3>
              <button
                onClick={() => { resetUserForm(); setShowUserModal(true); }}
                className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-4 py-2 rounded-lg font-medium hover:shadow-lg hover:shadow-blue-500/30 transition-all"
              >
                + {language === 'ja' ? 'ユーザー追加' : '添加用户'}
              </button>
            </div>

            <div className="bg-slate-700/50 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-700">
                  <tr>
                    <th className="text-left text-slate-400 text-sm font-medium px-6 py-3">用户名</th>
                    <th className="text-left text-slate-400 text-sm font-medium px-6 py-3">姓名</th>
                    <th className="text-left text-slate-400 text-sm font-medium px-6 py-3">角色</th>
                    <th className="text-left text-slate-400 text-sm font-medium px-6 py-3">权限</th>
                    <th className="text-left text-slate-400 text-sm font-medium px-6 py-3">{common.status}</th>
                    <th className="text-center text-slate-400 text-sm font-medium px-6 py-3">{common.actions}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-slate-700/50">
                      <td className="px-6 py-4">
                        <span className="text-white font-medium">{u.username}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-300">{u.name}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          u.role === 'admin' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {u.role === 'admin' ? copy.admin : copy.operator}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-400 text-sm">
                          {u.role === 'admin' ? '全部权限' : `${(u.permissions || []).length}项`}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          u.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 
                          u.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {u.status === 'active' ? '正常' : u.status === 'pending' ? '待审核' : '已暂停'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button onClick={() => openEditUserModal(u)} className="text-blue-400 hover:text-blue-300 mr-3">
                          {common.edit}
                        </button>
                        {u.status === 'active' ? (
                          <button onClick={() => handleUserStatusChange(u.id, 'suspended')} className="text-amber-400 hover:text-amber-300 mr-3">
                            暂停
                          </button>
                        ) : (
                          <button onClick={() => handleUserStatusChange(u.id, 'active')} className="text-emerald-400 hover:text-emerald-300 mr-3">
                            启用
                          </button>
                        )}
                        {u.username !== 'internal_admin' && (
                          <button onClick={() => handleDeleteUser(u.id)} className="text-red-400 hover:text-red-300">
                            {common.delete}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Email Notification Settings */}
        {activeSection === 'email' && user?.role === 'admin' && (
          <div className="space-y-6">
            <h3 className="text-white text-xl font-bold">{language === 'ja' ? 'メール通知設定' : '邮件提醒设置'}</h3>
            
            {/* Email Recipients */}
            <div className="bg-slate-700/50 rounded-lg p-4">
              <h4 className="text-white font-medium mb-3">{language === 'ja' ? '通知を受け取るメール' : '接收提醒的邮箱'}</h4>
              <div className="flex gap-2 mb-3">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder={language === 'ja' ? 'メールアドレスを入力' : '输入邮箱地址'}
                  className="flex-1 bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                  onKeyDown={(e) => e.key === 'Enter' && addEmail()}
                />
                <button onClick={addEmail} className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600">
                  {common.add}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {emailSettings.notification_emails.map((email, idx) => (
                  <span key={idx} className="bg-slate-600 text-white px-3 py-1 rounded-full text-sm flex items-center gap-2">
                    {email}
                    <button onClick={() => removeEmail(email)} className="text-red-400 hover:text-red-300">×</button>
                  </span>
                ))}
                {emailSettings.notification_emails.length === 0 && (
                  <span className="text-slate-500">{language === 'ja' ? 'メール未登録' : '未添加邮箱'}</span>
                )}
              </div>
            </div>

            {/* Notification Types */}
            <div className="space-y-3">
              <h4 className="text-white font-medium">{language === 'ja' ? '通知タイプ' : '提醒类型'}</h4>
              {[
                { key: 'license_expiry_notify', label: '驾照到期提醒', desc: '司机驾照即将到期时发送提醒' },
                { key: 'insurance_expiry_notify', label: '保险到期提醒', desc: '车辆保险即将到期时发送提醒' },
                { key: 'inspection_expiry_notify', label: '车检到期提醒', desc: '车辆车检即将到期时发送提醒' },
                { key: 'health_check_expiry_notify', label: '健康诊断到期提醒', desc: '司机健康诊断即将到期时发送提醒' },
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{item.label}</p>
                    <p className="text-slate-400 text-sm">{item.desc}</p>
                  </div>
                  <button
                    onClick={() => setEmailSettings({ ...emailSettings, [item.key]: !emailSettings[item.key] })}
                    className={`w-14 h-8 rounded-full transition-colors ${
                      emailSettings[item.key] ? 'bg-blue-500' : 'bg-slate-600'
                    }`}
                  >
                    <div className={`w-6 h-6 bg-white rounded-full transition-transform ${
                      emailSettings[item.key] ? 'translate-x-7' : 'translate-x-1'
                    }`}></div>
                  </button>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-slate-700">
              <button onClick={saveEmailSettings} className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-6 py-2 rounded-lg font-medium">
                {language === 'ja' ? '設定を保存' : '保存设置'}
              </button>
              <button onClick={sendExpiryAlerts} disabled={sendingAlert} className="bg-amber-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-amber-600 disabled:opacity-50">
                {sendingAlert ? (language === 'ja' ? '送信中...' : '发送中...') : (language === 'ja' ? '今すぐ通知送信' : '立即发送提醒')}
              </button>
            </div>
          </div>
        )}

        {/* Password Change */}
        {activeSection === 'password' && (
          <div className="space-y-6">
            <h3 className="text-white text-xl font-bold">{t.password}</h3>
            <form onSubmit={handlePasswordChange} className="max-w-md space-y-4">
              <div>
                <label className="block text-slate-400 text-sm mb-1">当前密码</label>
                <input
                  type="password"
                  value={passwordForm.old_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, old_password: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">新密码</label>
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">确认新密码</label>
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                  required
                />
              </div>
              <button
                type="submit"
                className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-6 py-2 rounded-lg font-medium"
              >
                {t.password}
              </button>
            </form>
          </div>
        )}

        {/* Company Info */}
        {activeSection === 'dispatch-documents' && user?.role === 'admin' && <DispatchDocumentSettings/>}
        {activeSection === 'company' && (
          <div className="space-y-6">
            <h3 className="text-white text-xl font-bold">{t.company}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-400 text-sm mb-1">公司名称</label>
                <input
                  type="text"
                  value={settings.companyName}
                  onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">假名</label>
                <input
                  type="text"
                  value={settings.companyNameKana}
                  onChange={(e) => setSettings({ ...settings, companyNameKana: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">电话</label>
                <input
                  type="tel"
                  value={settings.phone}
                  onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">邮箱</label>
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-slate-400 text-sm mb-1">地址</label>
                <textarea
                  value={settings.address}
                  onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                  rows={2}
                />
              </div>
            </div>
            <button onClick={handleSaveSettings} className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-8 py-3 rounded-lg font-medium">
              {language === 'ja' ? '設定を保存' : '保存设置'}
            </button>
          </div>
        )}

        {/* System */}
        {activeSection === 'system' && (
          <div className="space-y-6">
            <h3 className="text-white text-xl font-bold">{t.system}</h3>
            <div className="space-y-4">
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <p className="text-white font-medium">到期提醒天数</p>
                <p className="text-slate-400 text-sm mb-3">提前多少天发送到期提醒</p>
                <div className="flex items-center gap-2">
                  <input type="number" value={10} className="w-20 bg-slate-700 border border-slate-600 text-white px-3 py-2 rounded-lg text-center" disabled />
                  <span className="text-slate-400">天</span>
                </div>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <p className="text-white font-medium">系统版本</p>
                <p className="text-slate-400 text-sm">v1.0.0 - 湘南国際車隊管理システム</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700 max-h-[90vh] overflow-y-auto">
            <h3 className="text-white text-xl font-bold mb-6">
              {editingUser ? (language === 'ja' ? 'ユーザー編集' : '编辑用户') : (language === 'ja' ? 'ユーザー追加' : '添加用户')}
            </h3>
            <form onSubmit={handleUserSubmit} className="space-y-4">
              <div>
                <label className="block text-slate-400 text-sm mb-1">用户名 *</label>
                <input
                  type="text"
                  value={userForm.username}
                  onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                  required
                  disabled={!!editingUser}
                />
              </div>
              {!editingUser && (
                <div>
                  <label className="block text-slate-400 text-sm mb-1">密码 *</label>
                  <input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                    required={!editingUser}
                  />
                </div>
              )}
              <div>
                <label className="block text-slate-400 text-sm mb-1">姓名 *</label>
                <input
                  type="text"
                  value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">角色</label>
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                >
                  <option value="user">操作员</option>
                  <option value="admin">管理员</option>
                </select>
              </div>

              {/* 权限设置（仅操作员需要） */}
              {userForm.role === 'user' && (
                <div>
                  <label className="block text-slate-400 text-sm mb-2">操作员权限</label>
                  <div className="space-y-3">
                    {Object.entries(groupedPermissions).map(([group, perms]) => (
                      <div key={group} className="bg-slate-700/50 rounded-lg p-3">
                        <p className="text-slate-300 text-sm font-medium mb-2">{group}</p>
                        <div className="flex flex-wrap gap-2">
                          {perms.map(perm => (
                            <label key={perm.id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={userForm.permissions.includes(perm.id)}
                                onChange={() => togglePermission(perm.id)}
                                className="w-4 h-4 rounded bg-slate-600 border-slate-500"
                              />
                              <span className="text-slate-300 text-sm">{perm.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                <button
                  type="button"
                  onClick={() => { setShowUserModal(false); resetUserForm(); }}
                  className="px-6 py-2 text-slate-400 hover:text-white transition-colors"
                >
                  {common.cancel}
                </button>
                <button
                  type="submit"
                  className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-6 py-2 rounded-lg font-medium"
                >
                  {common.save}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetSettings;
